import React from 'react';
import { useField } from 'formik';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Checkbox, FormHelperText, FormLabel, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import isEqual from 'lodash/isEqual';
import { SessionFormField } from '@uplift/api';
import { YesNo, toCamel } from '@uplift/types';
import {
  Autocomplete,
  AutocompleteMulti, BaseTextField, ButtonStyleCheckbox,
  ButtonStyleCheckboxGroup, CheckboxWithLabel, DatePicker, getRenderTagsForAutocompleteMulti,
  HeightInput, MaskedInput, NumberInput,
  PillRadioButton, PillRadioButtonGroup,
  RadioControl, Select, TextInput,
  YesNoRadioButtonGroup,
} from '../../Inputs';
import { Markdown } from '../../Markdown';
import { Button } from '../../Button';
import { YesNoRadioControl } from './YesNoRadioControl.component';
import { DiagnosisAutocomplete } from './DiagnosisAutocomplete.component';
import { AutocompleteMultiSingleGroup } from './AutocompleteMultiSingleGroup.component';
import { DateFormats } from '@uplift/constants';
import { FieldToggleButton } from '../../FieldToggleButton';

const useStyles = makeStyles()({
  max336: {
    maxWidth: '336px'
  },
  max140: {
    maxWidth: '140px'
  },
  max180: {
    maxWidth: '180px',
  },
  max7125: {
    maxWidth: '7.125rem',
  },
  buttonStyleCheckboxGroup: {
    width: '100%',
  },
  noPaddingLeft: {
    paddingLeft: 0,
  },
  weightInput: {
    width: '100px',
  },
});


const useComboAlignAdornmentStyles = makeStyles({ name: 'ComboAlignAdornment' })(theme => ({
  inputRoot: {
    flexWrap: 'unset',
  },
  input: {
    flexGrow: 'unset',
  },
  endAdornment: {
    marginLeft: 0,
    '& > p': {
      color: theme.palette.text.primary
    }
  }
}));


export type ServerFieldProps = {
  name?: string;
  field: SessionFormField;
  color?: 'primary' | 'secondary';
  disabled?: boolean;
};


const StaticLabelField: React.FC<{ name: string; color?: ServerFieldProps['color'] }> = (props) => {
  const [ _field, meta ] = useField(props.name);

  return (
    <FormLabel
      color={props.color}
      error={!!meta.error}
    >
      {props.children}
    </FormLabel>
  );
};

const StaticHelperTextField: React.FC<{ name: string; color?: ServerFieldProps['color'] }> = (props) => {
  const [ _field, meta ] = useField(props.name);

  return (
    <FormHelperText
      style={{ fontSize: '0.850rem', marginTop: 0 }}
      color={props.color}
      error={!!meta.error}
    >
      {props.children}
    </FormHelperText>
  );
};


export const ServerField: React.FC<ServerFieldProps> = React.memo(({ name: _name, field, color, disabled }) => {
  const { classes: comboAlignAdornmentClasses } = useComboAlignAdornmentStyles();
  const { classes } = useStyles();
  const { label: _label, fieldType } = field;
  const name = _name || field.id;
  const fullWidth = field.layout.fullWidth;

  const label = <Markdown components={{ p: 'span' }}>{_label}</Markdown>;

  if (fieldType === 'static_helper_text') {
    return (
      <StaticHelperTextField name={name} color={color}>
        {label}
      </StaticHelperTextField>
    );
  } else if (fieldType === 'static_label') {
    return (
      <StaticLabelField name={name} color={color}>
        {label}
      </StaticLabelField>
    );
  } else if (fieldType === 'static_link') {
    return (
      <Box display="flex" justifyContent="center">
        <Button
          disabled={disabled}
          href={field.href}
          target="_blank"
          color="secondary"
          variant="contained"
        >
          {label}
        </Button>
      </Box>
    );
  } else if (fieldType === 'date') {
    const { renderAs } = field;

    return (
      <DatePicker
        disabled={disabled}
        name={name}
        label={label}
        color={color}
        helperText={field.dateFormat}
        className={
          (renderAs === 'inline_button' || renderAs === 'keyboard_only')
            ? classes.max180
            : classes.max140
        }
        format={field.dateFormat}
        valueFormat={DateFormats.ISO_DATE}
        keyboardOnly={renderAs !== 'picker_only'}
        showKeyboardPickerButton={renderAs === 'inline_button'}
      />
    );
  } else if (fieldType === 'select_string') {
    const { options, renderAs, valueDisplayText, valueDisplayTextAsLabel } = field;

    const values = options.map(o => {
      if (typeof o === 'string') {
        return {
          value: o,
          label: valueDisplayTextAsLabel ? valueDisplayText[toCamel(o)] : o,
          id: o
        };
      }

      return {
        value: o.value,
        label: valueDisplayTextAsLabel ? valueDisplayText[toCamel(o.value)] : o.value,
        id: o.value,
        disabled: o.disabled,
      };
    }).filter(o => !o.disabled);

    if (renderAs === 'radio') {
      return (
        <RadioControl
          disabled={disabled}
          color={color || 'primary'}
          label={label}
          name={name}
          values={values}
        />
      );
    } else if (renderAs === 'inline_radio') {
      return (
        <RadioControl
          disabled={disabled}
          color={color || 'primary'}
          label={label}
          name={name}
          inlineOptions
          values={values}
        />
      );
    } else {
      return (
        <Select
          disabled={disabled}
          label={label}
          name={name}
          items={values}
          color={color}
          autoWidth={true}
        />
      );
    }
  } else if (fieldType === 'boolean') {
    if (field.renderAs === 'yesNo') {
      return (
        <YesNoRadioButtonGroup
          disabled={disabled}
          asBoolean
          name={name}
          label={label}
          size="small"
        />
      );
    } else if (field.renderAs === 'checkbox') {
      return (
        <CheckboxWithLabel
          disabled={disabled}
          color={color}
          name={name}
          label={label}
        />
      );
    } else if (field.renderAs === 'toggleButton') {
      return (
        <Box className={classes.max336}>
          <FieldToggleButton
            name={name}
            label={label}
          />
        </Box>
      );
    }

    return (
      <YesNoRadioControl
        disabled={disabled}
        baseName=""
        fullName={name}
        label={label}
        inline
        color={color}
      />
    );
  } else if (fieldType === 'yes_or_other') {
    return (
      <PillRadioButtonGroup name={name} label={label} disabled={disabled}>
        <PillRadioButton size="small" label={YesNo.Yes} value={field.yesValue} isChecked={v => !!v && isEqual(v, field.yesValue)} icon={<CheckCircle />} />
        <PillRadioButton size="small" label={YesNo.No} value={field.noValue} isChecked={v => !!v && !isEqual(v, field.yesValue)} icon={<Cancel />} />
      </PillRadioButtonGroup>
    );
  } else if (fieldType === 'text_area') {
    return (
      <TextInput
        disabled={disabled}
        label={label}
        name={name}
        placeholder={field.placeholder}
        multiline
        minRows={field.minRows}
        fullWidth
        color={color}
        helperText={field.helperText}
      />
    );
  } else if (fieldType === 'text_field') {
    return (
      <TextInput
        disabled={disabled}
        label={label}
        name={name}
        placeholder={field.placeholder}
        className={fullWidth ? '' : classes.max336}
        fullWidth
        color={color}
        helperText={field.helperText}
      />
    );
  } else if (fieldType === 'select_multiple_string') {
    const { options, renderAs, helperText, allowCustomOption, noSelectionOptionText } = field;

    if (renderAs === 'checkbox') {
      const values = options.filter((option) => option !== 'Other').concat(allowCustomOption ? [ 'other' ] : []);

      return (
        <ButtonStyleCheckboxGroup
          disabled={disabled}
          label={label}
          name={name}
          color={color || 'primary'}
          noButton
          className={classes.buttonStyleCheckboxGroup}
          showError
          shouldGroup
          helperText={helperText ?? undefined}
          classes={{
            noButtonLabel: classes.noPaddingLeft,
            noButtonItems: classes.noPaddingLeft,
          }}
        >
          {values.map((option) => {
            if (option !== 'other') {
              return <ButtonStyleCheckbox disabled={disabled} label={option} value={option} key={option} />;
            } else {
              return (
                <ButtonStyleCheckbox
                  disabled={disabled}
                  key="other"
                  label="Other"
                  value="other"
                  other
                  color={color}
                  renderOtherInput={(props) => (
                    <BaseTextField
                      {...props}
                      fullWidth
                      placeholder=""
                    />
                  )}
                  otherPlacement="underLabel"
                />
              );
            }
          })}
        </ButtonStyleCheckboxGroup>
      );
    } else {
      if (field.exclusiveOn) {
        const values = field.options?.map((item) => {
          if (item === field.exclusiveOn) {
            return { id: item, value: item, label: item, groupId: 'exclusive' };
          }

          return { id: item, value: item, label: item };
        });

        return (
          <AutocompleteMultiSingleGroup
            disabled={disabled}
            color={color}
            extractId
            name={name}
            label={label}
            getOptionLabel={o => o.id}
            options={values}
            renderTags={getRenderTagsForAutocompleteMulti((opt) => opt?.label)}
          />
        );
      } else {
        const values = options.map((item) => ({ value: item, label: item, id: item }));

        return (
          <AutocompleteMulti
            disabled={disabled}
            label={label}
            name={name}
            getOptionLabel={(opt) => opt?.label}
            options={values}
            renderTags={getRenderTagsForAutocompleteMulti((opt) => opt?.label)}
            extractId
            helperText={helperText ?? undefined}
            placeholder={noSelectionOptionText ?? undefined}
            color={color}
            disableCloseOnSelect
            getOptionDisabled={undefined}
            renderOption={(option, state) => (
              <React.Fragment>
                <Checkbox style={{ marginRight: 8, padding: 2 }} checked={state.selected} color="secondary" /> {option.id}
              </React.Fragment>
            )}
          />
        );
      }
    }
  } else if (fieldType === 'integer') {
    if (field.renderAs === 'height') {
      return (
        <Box width="150px">
          <HeightInput
            disabled={disabled}
            color={color}
            name={name}
            label={label}
          />
        </Box>
      );
    } else if (field.renderAs === 'weight') {
      return (
        <NumberInput
          disabled={disabled}
          name={name}
          label={label}
          color={color}
          inputClasses={{ root: classes.weightInput }}
          helperText={field.helperText}
          slotProps={{
            input: {
              endAdornment: 'lbs'
            }
          }}
          numberInputProps={{
            decimalScale: 2,
            isAllowed: (values) => {
              if (values.floatValue === undefined) {
                return true;
              }
              if (typeof values.floatValue === 'number') {
                return values.floatValue < 1000;
              }

              return false;
            }
          }}
        />
      );
    } else if (field.renderAs === 'adorned') {
      const adornmentProp = field.adornment?.location === 'start'
        ? { startAdornment: field.adornment?.value }
        : { endAdornment: field.adornment?.value };

      return (
        <NumberInput
          disabled={disabled}
          name={name}
          label={label}
          color={color}
          helperText={field.helperText}
          slotProps={{
            input: adornmentProp
          }}
          numberInputProps={{
            decimalScale: 2,
            isAllowed: (values) => {
              if (values.floatValue === undefined) {
                return true;
              }
              if (typeof values.floatValue === 'number') {
                return values.floatValue < 1000;
              }

              return false;
            }
          }}
        />
      );
    }
  } else if (fieldType === 'diagnosis') {
    return (
      <DiagnosisAutocomplete
        disabled={disabled}
        color={color}
        name={name}
        label={label}
        helperText={field.helperText ?? undefined}
      />
    );
  } else if (fieldType === 'email') {
    return (
      <TextInput
        disabled={disabled}
        label={label}
        name={name}
        className={classes.max336}
        fullWidth
        color={color}
        helperText={field.helperText}
        type="email"
      />
    );
  } else if (fieldType === 'phone_number') {
    return (
      <MaskedInput
        disabled={disabled}
        label={label}
        name={name}
        fullWidth
        type="phone"
        className={classes.max336}
        color="secondary"
      />
    );
  }else if (fieldType === 'combo_box') {
    const { options, allowCustomOption, valueType } = field;

    if (field.value && !options.includes(field.value)) {
      options.push(field.value);

      // handle array of numbers
      if (valueType !== 'string') {
        options.sort((x, y) => Number(x) - Number(y));
      } else {
        options.sort((x, y) => x.localeCompare(y));
      }
    }

    const adornmentObj = {
      [field.adornment?.location === 'start' ? 'startAdornment' : 'endAdornment']: field.adornment?.value
    };

    return (
      <Autocomplete
        disabled={disabled}
        label={label}
        name={name}
        freeSolo={allowCustomOption}
        handleHomeEndKeys
        color="secondary"
        {...adornmentObj}
        options={options.map(x => {
          const opt = {
            id: x,
            label: x,
          };

          if (field.applyAdornmentToOptions) {
            opt.label = field.adornment?.location === 'start'
              ? `${field.adornment?.value} ${x}`
              : `${x} ${field.adornment?.value}`;
          }

          return opt;
        })}
        getOptionLabel={opt => {
          if (typeof opt === 'string') {
            return opt;
          }

          return opt.id;
        }}
        renderOption={(opt) => <Typography>{opt.label}</Typography>}
        className={classes.max7125}
        disableClearable
        classes={field.applyAdornmentToOptions ? comboAlignAdornmentClasses : undefined}
        transformValue={val => {
          if (typeof val === 'string') {
            return val;
          }

          return val?.id;
        }}
      />
    );
  }

  return (
    <div>Unknown Field, {JSON.stringify(field)}</div>
  );
});
